import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useUserData } from '../../../hooks/useUserData';
import { useLogout } from '../../../hooks/useLogout';

import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';


// Íconos
import { MdDashboard } from 'react-icons/md';
import { FaUserFriends } from 'react-icons/fa';
import { RiChat3Line } from 'react-icons/ri';
import { HiOutlineChartPie } from 'react-icons/hi';
import { AiFillHome } from 'react-icons/ai';
import { BiCommentDetail } from 'react-icons/bi';
import { GiGraduateCap } from 'react-icons/gi';
import { IoSettings, IoExit } from 'react-icons/io5';
import { CollectionsBookmark } from '@mui/icons-material';

import './styles.scss';
import { getCollections, stringToArray } from '../../../assets/js/functions';

export default function NestedList() {
	// Hooks de contexto
	const { user } = useAuth();
	const { userData, loading } = useUserData();
	const logout = useLogout();

	// Estados UI
	const [openAdministrar, setOpenAdministrar] = useState(false);
	const [openAprendizaje, setOpenAprendizaje] = useState(false);
	const [openUsuarios, setOpenUsuarios] = useState(false);
	const [openCursos, setOpenCursos] = useState(false);

	// Datos
	const [cursos, setCursos] = useState([]);

	const navigate = useNavigate();

	// Obtener sección actual de la URL
	const seccion = stringToArray(window.location.pathname, '/')[2];
	const seccionDropdown = stringToArray(window.location.pathname, '/')[3];

	useEffect(() => {
		if (userData) {
			getCursos();
		}

		// Abrir secciones según URL
		if (['usuarios', 'perfiles', 'dashboard', 'foros', 'reportes'].includes(seccion)) {
			setOpenAdministrar(true);
			setOpenAprendizaje(false);
		} else if (['subdisciplinas', 'home', 'curso'].includes(seccion)) {
			setOpenAprendizaje(true);
			setOpenAdministrar(false);
		}

		if (seccion === 'usuarios') setOpenUsuarios(true);
		if (seccion === 'curso') setOpenCursos(true);
	}, [userData, seccion]);

	// Obtener cursos dinámicos
	const getCursos = async () => {
		if (!userData) return;

		let array = [];
		const data = await getCollections(process.env.REACT_APP_BD_CURSOS);

		Object.values(data).map((val) => {
			if (val.activo) {
				if (
					val.perfil === 'ambos' ||
					val.perfil === userData.perfil ||
					['dev', 'superAdmin', 'administrador', 'Super Administrador'].includes(userData.perfil)
				) {
					array.push(val);
				}
			}
		});
		setCursos(array);
	};

	// Función navegación rápida
	const goUrl = (url) => window.location.replace(url);

	// Funciones toggle
	const toggleAdministrar = () => {
		setOpenAdministrar(!openAdministrar);
		if (!openAdministrar) setOpenAprendizaje(false);
	};

	const toggleAprendizaje = () => {
		setOpenAprendizaje(!openAprendizaje);
		if (!openAprendizaje) setOpenAdministrar(false);
	};

	const toggleUsuarios = () => setOpenUsuarios(!openUsuarios);
	const toggleCursos = () => setOpenCursos(!openCursos);

	return (
		<List
			sx={{ width: '100%', maxWidth: 500, bgcolor: 'background.paper' }}
			component='nav'
			aria-labelledby='nested-list-subheader'
			subheader={<ListSubheader component='div'>Menú</ListSubheader>}
		>
			{/* SECCIÓN ADMINISTRAR */}
			{userData?.perfil === 'Super Administrador' && (
				<>
					<ListItemButton onClick={toggleAdministrar}>
						<ListItemText primary='Administrar' />
						{openAdministrar ? <ExpandLess /> : <ExpandMore />}
					</ListItemButton>

					<Collapse in={openAdministrar} timeout='auto' unmountOnExit>
						<List component='div' disablePadding>
							{/* Dashboard */}
							<ListItemButton
								sx={{ pl: 4 }}
								onClick={() => goUrl('/admin/dashboard')}
								className={seccion === 'dashboard' ? 'active' : ''}
							>
								<ListItemIcon style={{ minWidth: '45px' }}>
									<MdDashboard size={20} />
								</ListItemIcon>
								<ListItemText primary='Dashboard' />
							</ListItemButton>

							{/* Usuarios */}
							<ListItemButton sx={{ pl: 4 }} onClick={toggleUsuarios}>
								<ListItemIcon style={{ minWidth: '45px' }}>
									<FaUserFriends size={20} />
								</ListItemIcon>
								<ListItemText primary='Usuarios' />
								{openUsuarios ? <ExpandLess /> : <ExpandMore />}
							</ListItemButton>

							<Collapse in={openUsuarios} timeout='auto' unmountOnExit>
								<List component='div' disablePadding>
									<ListItemButton
										sx={{ pl: 8 }}
										onClick={() => navigate('/admin/usuarios')}
										className={
											window.location.pathname === '/admin/usuarios' ||
											seccionDropdown === 'actualizar' ||
											seccionDropdown === 'crear'
												? 'active'
												: ''
										}
									>
										<ListItemText primary='Lista' />
									</ListItemButton>
									{/* <ListItemButton
										sx={{ pl: 8 }}
										onClick={() => navigate('/admin/usuarios/carga-masiva')}
										className={seccionDropdown === 'carga-masiva' ? 'active' : ''}
									>
										<ListItemText primary='Carga masiva' />
									</ListItemButton> */}
								</List>
							</Collapse>

							{/* Foros */}
							<ListItemButton
								sx={{ pl: 4 }}
								onClick={() => goUrl('/admin/foros')}
								className={seccion === 'foros' ? 'active' : ''}
							>
								<ListItemIcon style={{ minWidth: '45px' }}>
									<RiChat3Line size={20} />
								</ListItemIcon>
								<ListItemText primary='Foros' />
							</ListItemButton>

							{/* Reportes */}
							<ListItemButton
								sx={{ pl: 4 }}
								onClick={() => goUrl('/admin/reportes')}
								className={seccion === 'reportes' ? 'active' : ''}
							>
								<ListItemIcon style={{ minWidth: '45px' }}>
									<HiOutlineChartPie size={20} />
								</ListItemIcon>
								<ListItemText primary='Reportes' />
							</ListItemButton>
						</List>
					</Collapse>
				</>
			)}

			{/* Home */}
			<ListItemButton onClick={() => goUrl('/home')}>
				<ListItemIcon style={{ minWidth: '45px' }}>
					<AiFillHome size={20} />
				</ListItemIcon>
				<ListItemText primary='Home' />
			</ListItemButton>

			{/* Foro */}
			<ListItemButton onClick={() => navigate('/foro/subdisciplinas')}>
				<ListItemIcon style={{ minWidth: '45px' }}>
					<BiCommentDetail size={20} />
				</ListItemIcon>
				<ListItemText primary='Foro' />
			</ListItemButton>

			{/* Experto */}
			<ListItemButton onClick={() => goUrl('/home')}>
				<ListItemIcon style={{ minWidth: '45px' }}>
					<GiGraduateCap size={20} />
				</ListItemIcon>
				<ListItemText primary='Expertos' />
			</ListItemButton>

			{/* Configuración */}
			<ListItemButton onClick={() => goUrl('/admin/configuracion')}>
				<ListItemIcon style={{ minWidth: '45px' }}>
					<IoSettings size={20} />
				</ListItemIcon>
				<ListItemText primary='Configuración de mi perfil' />
			</ListItemButton>

			{/* Logout */}
			<ListItemButton onClick={logout}>
				<ListItemIcon style={{ minWidth: '45px' }}>
					<IoExit size={20} />
				</ListItemIcon>
				<ListItemText primary='Cerrar sesión' />
			</ListItemButton>
		</List>
	);
}
